import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { useAuth } from '../../hooks/useAuth';
import Input from '../../components/common/Input';
import Button from '../../components/common/Button';
import Select from '../../components/common/Select';
import Textarea from '../../components/common/Textarea';
import MapPicker from '../../components/common/MapPicker';
import Logo from '../../components/common/Logo';
import GoogleSignInButton from '../../components/auth/GoogleSignInButton';
import PhoneAccountHint from '../../components/common/PhoneAccountHint';
import {
  UserIcon,
  EnvelopeIcon,
  LockClosedIcon,
  BuildingStorefrontIcon,
  MapPinIcon,
  CheckCircleIcon,
  ChevronRightIcon,
  ChevronLeftIcon,
  CreditCardIcon,
} from '@heroicons/react/24/outline';
import { providersAPI } from '../../api/providers';
import { paymentsAPI } from '../../api/payments';
import { authAPI } from '../../api/auth';
import mtnLogo from '../../assets/mtn-logo.svg';
import airtelLogo from '../../assets/airtel-logo.svg';
import toast from 'react-hot-toast';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';

const STEPS = [
  { id: 1, title: 'Account', description: 'Login details' },
  { id: 2, title: 'Business', description: 'Business info' },
  { id: 3, title: 'Location', description: 'Your area' },
  { id: 4, title: 'Settings', description: 'Preferences' },
  { id: 5, title: 'Payment', description: 'Registration fee' },
];

const LANGUAGES = [
  { id: 'en', name: 'English' },
  { id: 'sw', name: 'Swahili' },
  { id: 'lg', name: 'Luganda' },
  { id: 'xog', name: 'Lusoga' },
  { id: 'nyn', name: 'Runyankole' },
  { id: 'cgg', name: 'Rukiga' },
  { id: 'nyo', name: 'Runyoro' },
  { id: 'ttj', name: 'Rutooro' },
  { id: 'ach', name: 'Acholi' },
  { id: 'laj', name: 'Lango' },
  { id: 'teo', name: 'Ateso' },
  { id: 'lgg', name: 'Lugbara' },
  { id: 'myx', name: 'Lumasaba' },
  { id: 'kdj', name: 'Karamojong' },
];

// Anchor towns for regional language detection — nearest anchor wins.
// Covers Uganda's four regions and their dominant local languages.
const REGIONAL_ANCHORS = [
  // Central — Luganda
  { lat: 0.3476, lng: 32.5825, lang: 'lg' },   // Kampala
  { lat: 0.0512, lng: 32.4637, lang: 'lg' },   // Entebbe
  { lat: 0.3533, lng: 32.7553, lang: 'lg' },   // Mukono
  { lat: -0.3373, lng: 31.7340, lang: 'lg' },  // Masaka
  { lat: 0.4478, lng: 33.2026, lang: 'lg' },   // Lugazi side
  // Eastern
  { lat: 0.4244, lng: 33.2041, lang: 'xog' },  // Jinja — Lusoga
  { lat: 0.6092, lng: 33.4686, lang: 'xog' },  // Iganga — Lusoga
  { lat: 1.0784, lng: 34.1755, lang: 'myx' },  // Mbale — Lumasaba
  { lat: 1.7147, lng: 33.6111, lang: 'teo' },  // Soroti — Ateso
  { lat: 1.4608, lng: 33.9361, lang: 'teo' },  // Kumi — Ateso
  // Northern
  { lat: 2.7746, lng: 32.2990, lang: 'ach' },  // Gulu — Acholi
  { lat: 3.2783, lng: 32.8867, lang: 'ach' },  // Kitgum — Acholi
  { lat: 2.2499, lng: 32.8998, lang: 'laj' },  // Lira — Lango
  { lat: 3.0201, lng: 30.9111, lang: 'lgg' },  // Arua — Lugbara
  { lat: 2.5344, lng: 34.6668, lang: 'kdj' },  // Moroto — Karamojong
  // Western
  { lat: -0.6072, lng: 30.6545, lang: 'nyn' }, // Mbarara — Runyankole
  { lat: -1.2413, lng: 29.9852, lang: 'cgg' }, // Kabale — Rukiga
  { lat: 0.6710, lng: 30.2748, lang: 'ttj' },  // Fort Portal — Rutooro
  { lat: 0.1866, lng: 30.0788, lang: 'ttj' },  // Kasese — Rutooro
  { lat: 1.4331, lng: 31.3524, lang: 'nyo' },  // Hoima — Runyoro
  { lat: 1.6745, lng: 31.7150, lang: 'nyo' },  // Masindi — Runyoro
];

const detectRegionalLanguage = (lat, lng) => {
  if (typeof lat !== 'number' || typeof lng !== 'number') return null;
  let best = null;
  let bestDist = Infinity;
  for (const anchor of REGIONAL_ANCHORS) {
    const d = (anchor.lat - lat) ** 2 + (anchor.lng - lng) ** 2;
    if (d < bestDist) {
      bestDist = d;
      best = anchor.lang;
    }
  }
  return best;
};

const GENDER_OPTIONS = [
  { value: 'female', label: 'Female Clients Only' },
  { value: 'male', label: 'Male Clients Only' },
  { value: 'all', label: 'All Genders' },
];

const SPECIALIZATIONS = [
  { id: 'hair', name: 'Hair Styling' },
  { id: 'nails', name: 'Nail Care' },
  { id: 'makeup', name: 'Makeup Artistry' },
  { id: 'facial', name: 'Facial Treatments' },
  { id: 'massage', name: 'Massage Therapy' },
  { id: 'waxing', name: 'Waxing/Depilation' },
  { id: 'pedicure', name: 'Pedicure' },
  { id: 'barber', name: 'Barbering' },
];

const getCategoryDescription = (categories, categoryId) => {
  const selected = categories.find((cat) => String(cat.id) === String(categoryId));
  return selected?.description || selected?.desc || '';
};

// Only Gmail addresses are accepted — standard, verifiable format
const GMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@gmail\.com$/i;

const PASSWORD_REQUIREMENTS = [
  { key: 'length', label: 'At least 12 characters long' },
  { key: 'uppercase', label: 'At least one uppercase letter (A-Z)' },
  { key: 'lowercase', label: 'At least one lowercase letter (a-z)' },
  { key: 'number', label: 'At least one number (0-9)' },
  { key: 'special', label: 'At least one special character (!@#$%^&*)' },
  { key: 'noThreeRepeats', label: 'No character repeated 3 times in a row' },
];

const validatePassword = (password) => ({
  length: password.length >= 12,
  uppercase: /[A-Z]/.test(password),
  lowercase: /[a-z]/.test(password),
  number: /[0-9]/.test(password),
  special: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password),
  noThreeRepeats: password.length > 0 && !/(.)\1{2,}/.test(password),
});

const PasswordChecklist = ({ checks }) => (
  <div style={{
    padding: '12px',
    borderRadius: '8px',
    backgroundColor: 'var(--gray-2)',
    border: '1px solid var(--gray-4)',
    fontFamily: "'Poppins', sans-serif",
  }}>
    <p style={{ margin: '0 0 8px', fontSize: '13px', fontWeight: 600, color: 'var(--gray-12)' }}>
      Password requirements
    </p>
    <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: '6px' }}>
      {PASSWORD_REQUIREMENTS.map(({ key, label }) => {
        const achieved = checks[key];
        return (
          <li
            key={key}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '12px',
              lineHeight: 1.35,
              color: achieved ? '#16a34a' : 'var(--gray-10)',
              transition: 'color 0.15s ease',
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: '16px',
                height: '16px',
                borderRadius: '50%',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                border: achieved ? '1px solid #16a34a' : '1px solid var(--gray-7)',
                backgroundColor: achieved ? '#16a34a' : 'transparent',
                color: '#fff',
                fontSize: '11px',
                fontWeight: 700,
              }}
            >
              {achieved ? '✓' : ''}
            </span>
            <span>{label}</span>
          </li>
        );
      })}
    </ul>
  </div>
);

// Defined outside any component — a stable reference so PhoneInput never remounts the input
const PhoneNumberInput = React.forwardRef((props, ref) => (
  <input {...props} ref={ref} autoComplete="tel-national" />
));

/* ─── PhoneField ──────────────────────────────────────────────────────────── */
function PhoneField({ value, onChange, error, className, style }) {
  const [country, setCountry] = useState('UG');

  const handleCountryChange = (c) => {
    setCountry(c || 'UG');
    onChange('');
  };

  const valid = value ? (() => { try { return isValidPhoneNumber(value); } catch { return false; } })() : true;

  return (
    <div>
      <PhoneInput
        international
        countryCallingCodeEditable={false}
        defaultCountry="UG"
        country={country}
        onCountryChange={handleCountryChange}
        value={value}
        onChange={(val) => onChange(val || '')}
        className={className}
        style={style}
        inputComponent={PhoneNumberInput}
      />
      {error && <p style={{ color: 'var(--red-9)', fontSize: '12px', marginTop: '4px' }}>{error}</p>}
      {value && !valid && !error && (
        <p style={{ color: 'var(--red-9)', fontSize: '12px', marginTop: '4px' }}>
          Invalid number for selected country
        </p>
      )}
    </div>
  );
}

const DRAFT_KEY = 'salon_register_draft';

const loadDraft = () => {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const saveDraft = (formData, currentStep, feeStatus) => {
  try {
    const { password, confirmPassword, ...safe } = formData;
    localStorage.setItem(DRAFT_KEY, JSON.stringify({ formData: safe, currentStep, feeStatus }));
  } catch {}
};

const clearDraft = () => { try { localStorage.removeItem(DRAFT_KEY); } catch {} };

// Must match the plans on /register/provider/plans and the backend PLAN_PRICES
const PLAN_INFO = {
  ORDINARY: { label: 'Ordinary', price: 20000 },
  ADVANCED: { label: 'Advanced', price: 30000 },
  PREMIUM: { label: 'Premium', price: 80000 },
};

const DEFAULT_FORM = {
  name: '',
  email: '',
  phone: '',
  plan: 'ORDINARY',
  password: '',
  confirmPassword: '',
  role: 'provider',
  businessName: '',
  category: '',
  description: '',
  specializations: [],
  latitude: null,
  longitude: null,
  shopAddress: '',
  businessLocation: '',
  offersMobile: false,
  maxTravelKm: 10,
  languages: ['en'],
  genderPreference: 'all',
  hygieneNotes: '',
  autoAcceptBookings: false,
  bufferTimeMinutes: 15,
};

const Register = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { register } = useAuth();
  const [loading, setLoading] = useState(false);
  const accountType = location.pathname.includes('/client') ? 'client' : 'provider';
  const [feePolling, setFeePolling] = useState(false);
  const pollRef = useRef(null);

  const draft = loadDraft();
  // Always start at step 1 when restoring a draft — password is never saved,
  // so the user must re-enter it before proceeding.
  const [currentStep, setCurrentStep] = useState(1);
  const [feeStatus, setFeeStatus] = useState(draft?.feeStatus || null);
  const [paymentMethod, setPaymentMethod] = useState('mtn');
  // The MoMo number charged can differ from the registration number
  // (captive-portal style: pay with any number you like)
  const [paymentPhone, setPaymentPhone] = useState('');
  const [paymentPhoneError, setPaymentPhoneError] = useState('');
  // Hosted card checkout URL — shown as a tappable button because mobile
  // browsers block window.open() calls that happen after an await
  const [checkoutUrl, setCheckoutUrl] = useState(null);
  const [formData, setFormData] = useState({ ...DEFAULT_FORM, ...(draft?.formData || {}) });

  const [categories, setCategories] = useState([]);
  const [errors, setErrors] = useState({});
  const passwordChecks = validatePassword(formData.password);
  const passwordMeetsRequirements = Object.values(passwordChecks).every(Boolean);

  useEffect(() => {
    providersAPI.getCategories().then(data => setCategories(Array.isArray(data) ? data : [])).catch(() => {});
  }, []);

  // Plan selected on /register/provider/plans arrives via navigation state;
  // stored in formData so it survives page refresh through the draft.
  useEffect(() => {
    const chosen = location.state?.plan;
    if (chosen && PLAN_INFO[chosen]) {
      setFormData((prev) => (prev.plan === chosen ? prev : { ...prev, plan: chosen }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // Prefill the payment number with the registration number when reaching the payment step
  useEffect(() => {
    if (currentStep === 5 && !paymentPhone && formData.phone) {
      setPaymentPhone(formData.phone);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  const activePlan = PLAN_INFO[formData.plan] ? formData.plan : 'ORDINARY';
  const planLabel = PLAN_INFO[activePlan].label;
  const planPrice = PLAN_INFO[activePlan].price;
  const planPriceText = `UGX ${planPrice.toLocaleString()}`;

  useEffect(() => {
    saveDraft(formData, currentStep, feeStatus);
  }, [formData, currentStep, feeStatus]);

  // Live email check — waits 600ms after typing stops, then verifies availability
  useEffect(() => {
    const email = formData.email.trim();
    if (!email) return undefined;
    if (!GMAIL_REGEX.test(email)) {
      // Only flag as invalid once it looks like a complete address
      if (/\S+@\S+\.\S+/.test(email)) {
        setErrors((prev) => ({ ...prev, email: 'Only Gmail addresses are accepted (yourname@gmail.com)' }));
      }
      return undefined;
    }
    const timer = setTimeout(async () => {
      try {
        const result = await authAPI.checkAvailability({ email });
        if (result.email && !result.email.available) {
          setErrors((prev) => ({ ...prev, email: 'This email is already registered. Please sign in.' }));
        }
      } catch (_) { /* network error — final submit will catch it */ }
    }, 600);
    return () => clearTimeout(timer);
  }, [formData.email]);

  // Live phone check — availability first, then MakyPay mobile-money validity
  useEffect(() => {
    const phone = formData.phone;
    if (!phone || !isValidPhoneNumber(phone)) return undefined;
    const timer = setTimeout(async () => {
      try {
        const result = await authAPI.checkAvailability({ phone });
        if (result.phone && !result.phone.available) {
          setErrors((prev) => ({ ...prev, phone: 'This phone number is already registered. Please sign in.' }));
          return;
        }
        const check = await paymentsAPI.verifyPhone(phone);
        if (check && check.valid === false) {
          setErrors((prev) => ({ ...prev, phone: check.message || 'This number is not registered for mobile money.' }));
        }
      } catch (_) { /* network error — final submit will catch it */ }
    }, 600);
    return () => clearTimeout(timer);
  }, [formData.phone]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'category') {
      setFormData({
        ...formData,
        category: value,
        description: getCategoryDescription(categories, value),
      });
      if (errors.category || errors.description) {
        setErrors({ ...errors, category: '', description: '' });
      }
      return;
    }

    if (type === 'checkbox') {
      setFormData({ ...formData, [name]: checked });
    } else {
      setFormData({ ...formData, [name]: value });
    }

    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const handleLocationSelect = (lat, lng, address) => {
    // Auto-add the dominant regional language for the picked area (English stays default)
    const regionalLang = detectRegionalLanguage(lat, lng);
    setFormData((prev) => ({
      ...prev,
      latitude: lat,
      longitude: lng,
      shopAddress: address || prev.shopAddress,
      businessLocation: address || prev.businessLocation,
      languages: regionalLang && !prev.languages.includes(regionalLang)
        ? [...prev.languages, regionalLang]
        : prev.languages,
    }));
    if (errors.mapPicker) {
      setErrors({ ...errors, mapPicker: '' });
    }
  };

  const handleClientSubmit = async () => {
    if (!validateStep(1)) {
      return;
    }

    setLoading(true);
    try {
      const registerData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
        role: 'client',
      };

      await register(registerData);
      toast.success('Registration successful! Please login.');
      navigate('/login');
    } catch (error) {
      console.error('Registration error:', error);
      toast.error('Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      if (!formData.name.trim()) newErrors.name = 'Name is required';
      if (!formData.email.trim()) {
        newErrors.email = 'Email is required';
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = 'Email is invalid';
      } else if (!GMAIL_REGEX.test(formData.email.trim())) {
        newErrors.email = 'Only Gmail addresses are accepted (yourname@gmail.com)';
      }
      if (!formData.phone) {
        newErrors.phone = 'Phone number is required';
      } else if (!isValidPhoneNumber(formData.phone)) {
        newErrors.phone = 'Please enter a valid phone number';
      }
      if (!formData.password) {
        newErrors.password = 'Password is required';
      } else if (!passwordMeetsRequirements) {
        newErrors.password = 'Please meet all password requirements';
      }
      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }

    if (step === 2) {
      if (!formData.businessName.trim()) newErrors.businessName = 'Business name is required';
      if (!formData.category) newErrors.category = 'Category is required';
    }

    if (step === 3) {
      if (!formData.latitude || !formData.longitude) {
        newErrors.mapPicker = 'Please select your business location on the map';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = async () => {
    if (!validateStep(currentStep)) return;

    // Check email + phone uniqueness on Step 1 before the user spends time on later steps
    if (currentStep === 1) {
      try {
        const result = await authAPI.checkAvailability({ email: formData.email, phone: formData.phone });
        const newErrors = {};
        if (result.email && !result.email.available) {
          newErrors.email = 'An account with this email already exists. Please sign in.';
        }
        if (result.phone && !result.phone.available) {
          newErrors.phone = 'This phone number is already registered. Please sign in.';
        }
        if (Object.keys(newErrors).length > 0) {
          setErrors(prev => ({ ...prev, ...newErrors }));
          return;
        }
      } catch (_) {
        // Network error — let them proceed; the final submit will catch it
      }
    }

    setCurrentStep(currentStep + 1);
  };

  const handleBack = () => {
    setCurrentStep(currentStep - 1);
  };

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    setFeePolling(false);
  };

  const checkFeeStatusOnce = async () => {
    setLoading(true);
    try {
      const status = await paymentsAPI.getRegistrationFeeStatus(formData.email);
      setFeeStatus(status.status);
      if (status.status === 'FAILED') {
        toast.error('Payment failed. Please try again.');
      } else if (status.status !== 'COMPLETED') {
        toast('Still processing — give it a moment and check again.', { icon: '⏳' });
      }
    } catch (_) { /* keep current state */ }
    finally { setLoading(false); }
  };

  const handleCancelPayment = async () => {
    stopPolling();
    try {
      await paymentsAPI.cancelRegistrationFee(formData.email);
    } catch (_) {}
    setFeeStatus(null);
    setCheckoutUrl(null);
  };

  const handlePayFee = async () => {
    if (paymentMethod !== 'card') {
      if (!paymentPhone || !isValidPhoneNumber(paymentPhone)) {
        setPaymentPhoneError('Enter a valid mobile money number to pay with');
        return;
      }
      setPaymentPhoneError('');
    }
    setLoading(true);
    // Optimistic: show the waiting panel the instant Pay is tapped — the
    // initiate call runs behind it and we revert if it fails.
    setFeeStatus('PENDING');
    try {
      const result = await paymentsAPI.initiateRegistrationFee({
        email: formData.email,
        phone: paymentMethod === 'card' ? undefined : paymentPhone,
        method: paymentMethod,
        plan: activePlan,
      });
      setFeeStatus(result.status);

      if (result.status === 'COMPLETED') return;

      // Card: try to open hosted checkout in a new tab (often blocked on
      // mobile after an await) and keep the URL for the tappable button below
      if (result.checkoutUrl) {
        setCheckoutUrl(result.checkoutUrl);
        window.open(result.checkoutUrl, '_blank', 'noopener,noreferrer');
      }

      // Poll every 2.5 s so confirmation lands fast. Cards clear in ~3–5 min
      // on MakyPay's side (10 min window); MoMo prompts expire fast (3 min).
      setFeePolling(true);
      let attempts = 0;
      const MAX_ATTEMPTS = paymentMethod === 'card' ? 240 : 72;
      pollRef.current = setInterval(async () => {
        attempts += 1;
        try {
          const status = await paymentsAPI.getRegistrationFeeStatus(formData.email);
          setFeeStatus(status.status);
          if (status.status === 'COMPLETED' || status.status === 'FAILED') {
            stopPolling();
            if (status.status === 'FAILED') {
              toast.error('Payment failed. Please try again.');
            }
            return;
          }
        } catch (err) {
          // 404 means the record was cancelled — stop silently
          if (err?.response?.status === 404) {
            stopPolling();
            return;
          }
        }

        if (attempts >= MAX_ATTEMPTS) {
          // Don't declare failure — the charge may still clear. Leave the
          // record intact and let the user check manually.
          stopPolling();
          toast('Payment is taking longer than usual. Complete it, then tap "Check payment status".', { icon: '⏳' });
        }
      }, 2500);
    } catch (err) {
      setFeeStatus(null); // revert the optimistic waiting panel
      const errCode = err?.response?.data?.error;
      const errMsg  = err?.response?.data?.message;
      if (errCode === 'An account already exists for this email.') {
        toast.error('An account already exists for this email. Please sign in.');
      } else if (errCode === 'INVALID_PHONE' || errCode === 'PAYMENT_INIT_FAILED') {
        toast.error(errMsg || 'That number is not registered for mobile money. Please check and try again.');
      } else {
        toast.error('Could not initiate payment. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleProviderSubmit = async (e) => {
    e.preventDefault();

    if (!validateStep(currentStep)) {
      return;
    }

    setLoading(true);

    try {
      const registerData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
        role: 'provider',
        providerProfile: {
          businessName: formData.businessName,
          category: formData.category,
          description: formData.description,
          specializations: formData.specializations,
          location: formData.businessLocation || formData.shopAddress,
          shopAddress: formData.shopAddress,
          lat: formData.latitude,
          lng: formData.longitude,
          offersMobile: formData.offersMobile,
          maxTravelKm: formData.offersMobile ? formData.maxTravelKm : null,
          languages: formData.languages,
          // Provider service expects any | male_only | female_only
          genderPreference: { all: 'any', female: 'female_only', male: 'male_only' }[formData.genderPreference] || 'any',
          hygieneNotes: formData.hygieneNotes,
          autoAcceptBookings: formData.autoAcceptBookings,
          bufferTimeMinutes: formData.bufferTimeMinutes,
        },
      };

      await register(registerData);
      clearDraft();
      toast.success('Submitted! We\'ll review your profile shortly.');
      navigate('/login');
    } catch (error) {
      console.error('Registration error:', error);
      const errCode = error?.response?.data?.error;
      if (errCode === 'REGISTRATION_FEE_REQUIRED') {
        toast.error('Payment not confirmed. Please complete payment first.');
        setFeeStatus(null);
        setCurrentStep(5);
      } else if (errCode === 'USER_ALREADY_EXISTS') {
        const field = error?.response?.data?.field;
        toast.error(field === 'phone'
          ? 'This phone number is already registered. Please sign in.'
          : 'An account with this email already exists. Please sign in.');
      } else {
        toast.error('Registration failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleSpecialization = (id) => {
    const current = formData.specializations;
    if (current.includes(id)) {
      setFormData({ ...formData, specializations: current.filter(s => s !== id) });
    } else {
      setFormData({ ...formData, specializations: [...current, id] });
    }
  };

  const toggleLanguage = (id) => {
    const current = formData.languages;
    if (current.includes(id)) {
      setFormData({ ...formData, languages: current.filter(l => l !== id) });
    } else {
      setFormData({ ...formData, languages: [...current, id] });
    }
  };

  return (
    <div className="auth-page" style={{ minHeight: '100vh', display: 'flex' }}>
      {/* LEFT — Registration form */}
      <div
        className="flex-1 flex flex-col px-5 py-6 sm:px-8 sm:py-10"
        style={{ backgroundColor: 'var(--color-background)', overflowY: 'auto', maxHeight: '100vh' }}
      >
        <div className="flex items-center justify-between gap-4">
          <Logo size="small" dark />
          <Link
            to="/"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px',
              fontSize: '14px', color: 'var(--gray-10)', textDecoration: 'none',
              fontWeight: 500, fontFamily: "'Poppins', sans-serif",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--orange-9)')}
            onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--gray-10)')}
          >
            <span style={{ fontSize: '18px', lineHeight: 1 }}>←</span> Back to home
          </Link>
        </div>

        <div className="flex-1 flex items-start justify-center pt-8">
          <div style={{ width: '100%', maxWidth: '420px' }}>

            <div className="mb-8 text-center">
              <h1
                className="text-2xl sm:text-3xl md:text-[32px] font-bold mb-2"
                style={{ color: 'var(--gray-12)', fontFamily: "'Poppins', sans-serif" }}
              >
                Sign Up
              </h1>
              <p
                className="text-sm sm:text-[15px]"
                style={{ color: 'var(--gray-10)', margin: 0, fontFamily: "'Poppins', sans-serif" }}
              >
                {accountType === 'provider' ? 'Start growing your business today' : 'Your beauty journey begins here'}
              </p>
            </div>

            {accountType === 'provider' && (
              <div style={{ marginBottom: '24px', fontFamily: "'Poppins', sans-serif" }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '10px' }}>
                  <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--gray-12)' }}>
                    {STEPS[currentStep - 1].title}
                  </span>
                  <span style={{ fontSize: '12px', color: 'var(--gray-10)' }}>
                    Step {currentStep} of {STEPS.length}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: '6px' }}>
                  {STEPS.map((step) => (
                    <div
                      key={step.id}
                      style={{
                        flex: 1, height: '4px', borderRadius: '999px',
                        backgroundColor: currentStep >= step.id ? 'var(--orange-9)' : 'var(--gray-4)',
                        transition: 'background-color 0.3s ease',
                      }}
                    />
                  ))}
                </div>
              </div>
            )}

            <div>
              {accountType === 'client' ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  <Input
                    label="Full Name"
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    icon={<UserIcon style={{ width: '20px', height: '20px', color: 'var(--gray-8)' }} />}
                    error={errors.name}
                    placeholder="Your full name"
                    required
                  />

                  <Input
                    label="Email Address"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    icon={<EnvelopeIcon style={{ width: '20px', height: '20px', color: 'var(--gray-8)' }} />}
                    error={errors.email}
                    placeholder="your@email.com"
                    required
                  />

                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', color: 'var(--gray-12)', fontSize: '14px', fontWeight: 500 }}>
                      Phone Number <span style={{ color: 'var(--red-9)' }}>*</span>
                    </label>
                      <PhoneField
                        value={formData.phone}
                        onChange={(value) => {
                          setFormData({ ...formData, phone: value || '' });
                          if (errors.phone) setErrors({ ...errors, phone: '' });
                        }}
                        error={errors.phone}
                        className="phone-input-field"
                        style={{ width: '100%', borderRadius: '8px', backgroundColor: '#ffffff' }}
                      />
                      <PhoneAccountHint phone={formData.phone} />
                  </div>

                  <Input
                    label="Password"
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    icon={<LockClosedIcon style={{ width: '20px', height: '20px', color: 'var(--gray-8)' }} />}
                    error={errors.password}
                    placeholder="Create a strong password"
                    autoComplete="new-password"
                    showPasswordToggle
                    required
                  />

                  <PasswordChecklist checks={passwordChecks} />

                  <Input
                    label="Confirm Password"
                    type="password"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    icon={<LockClosedIcon style={{ width: '20px', height: '20px', color: 'var(--gray-8)' }} />}
                    error={errors.confirmPassword}
                    placeholder="Re-enter password"
                    autoComplete="new-password"
                    showPasswordToggle
                    required
                  />

                  <Button type="button" onClick={handleClientSubmit} loading={loading} fullWidth style={{ marginTop: '4px' }}>
                    Create Account
                  </Button>

                  {/* OR divider */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '8px 0' }}>
                    <div style={{ flex: 1, height: '1px', backgroundColor: 'var(--gray-4)' }} />
                    <span style={{ fontSize: '11px', letterSpacing: '0.2em', color: 'var(--gray-9)', textTransform: 'uppercase' }}>Or</span>
                    <div style={{ flex: 1, height: '1px', backgroundColor: 'var(--gray-4)' }} />
                  </div>

                  {/* Social button */}
                  <div className="grid grid-cols-1 gap-3">
                    <GoogleSignInButton compact />
                  </div>
                </div>
              ) : (
                <form onSubmit={handleProviderSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  {currentStep === 1 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                      <Input
                        label="Full Name"
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        icon={<UserIcon style={{ width: '20px', height: '20px', color: 'var(--gray-8)' }} />}
                        error={errors.name}
                        placeholder="Your full name"
                        required
                      />

                      <Input
                        label="Email Address"
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        icon={<EnvelopeIcon style={{ width: '20px', height: '20px', color: 'var(--gray-8)' }} />}
                        error={errors.email}
                        placeholder="your@email.com"
                        required
                      />

                      <div>
                        <label style={{ display: 'block', marginBottom: '6px', color: 'var(--gray-12)', fontSize: '14px', fontWeight: 500 }}>
                          Phone Number <span style={{ color: 'var(--red-9)' }}>*</span>
                        </label>
                        <PhoneField
                          value={formData.phone}
                          onChange={(value) => {
                            setFormData({ ...formData, phone: value || '' });
                            if (errors.phone) setErrors({ ...errors, phone: '' });
                          }}
                          error={errors.phone}
                          className="phone-input-field"
                          style={{ width: '100%', borderRadius: '8px', backgroundColor: '#ffffff' }}
                        />
                        <PhoneAccountHint phone={formData.phone} />
                      </div>

                      <Input
                        label="Password"
                        type="password"
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        icon={<LockClosedIcon style={{ width: '20px', height: '20px', color: 'var(--gray-8)' }} />}
                        error={errors.password}
                        placeholder="Create a strong password"
                        autoComplete="new-password"
                        showPasswordToggle
                        required
                      />

                      <PasswordChecklist checks={passwordChecks} />

                      <Input
                        label="Confirm Password"
                        type="password"
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        icon={<LockClosedIcon style={{ width: '20px', height: '20px', color: 'var(--gray-8)' }} />}
                        error={errors.confirmPassword}
                        placeholder="Re-enter password"
                        autoComplete="new-password"
                        showPasswordToggle
                        required
                      />
                    </div>
                  )}

                  {currentStep === 2 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                      <Input
                        label="Business Name"
                        type="text"
                        name="businessName"
                        value={formData.businessName}
                        onChange={handleChange}
                        icon={<BuildingStorefrontIcon style={{ width: '20px', height: '20px', color: 'var(--gray-8)' }} />}
                        error={errors.businessName}
                        placeholder="e.g., Elite Beauty Salon"
                        required
                      />

                      <Select
                        label="Business Category"
                        name="category"
                        value={formData.category}
                        onChange={handleChange}
                        options={categories.map((cat) => ({ value: cat.id, label: cat.name }))}
                        placeholder={categories.length === 0 ? 'Loading categories...' : 'Select a category'}
                        disabled={categories.length === 0}
                        error={errors.category}
                        required
                      />

                      <Textarea
                        label="Description"
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        placeholder="Tell clients about your services, experience, etc..."
                        rows={4}
                      />

                      <div>
                        <label style={{ display: 'block', marginBottom: '10px', color: 'var(--gray-12)', fontSize: '14px', fontWeight: 500 }}>
                          Specializations
                        </label>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
                          {SPECIALIZATIONS.map((spec) => (
                            <button
                              key={spec.id}
                              type="button"
                              onClick={() => toggleSpecialization(spec.id)}
                              style={{
                                padding: '12px',
                                borderRadius: '8px',
                                border: formData.specializations.includes(spec.id) ? '2px solid var(--orange-9)' : '1px solid var(--gray-5)',
                                backgroundColor: formData.specializations.includes(spec.id) ? 'var(--orange-1)' : 'transparent',
                                color: formData.specializations.includes(spec.id) ? 'var(--orange-10)' : 'var(--gray-10)',
                                fontSize: '14px',
                                fontWeight: 500,
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                              }}
                            >
                              {spec.name}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {currentStep === 3 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: '10px', color: 'var(--gray-12)', fontSize: '14px', fontWeight: 500 }}>
                          Select Location on Map <span style={{ color: 'var(--red-9)' }}>*</span>
                        </label>
                        <MapPicker
                          onLocationSelect={handleLocationSelect}
                          initialLat={formData.latitude || undefined}
                          initialLng={formData.longitude || undefined}
                          initialAddress={formData.shopAddress}
                        />
                        {errors.mapPicker && (
                          <p style={{ marginTop: '6px', fontSize: '12px', color: 'var(--red-9)' }}>{errors.mapPicker}</p>
                        )}
                        {formData.latitude && formData.longitude && (
                          <p style={{ marginTop: '6px', fontSize: '12px', color: 'var(--green-10)' }}>
                            ✓ Location set
                          </p>
                        )}
                      </div>

                      <Input
                        label="Address Label"
                        type="text"
                        name="shopAddress"
                        value={formData.shopAddress}
                        onChange={handleChange}
                        icon={<MapPinIcon style={{ width: '20px', height: '20px', color: 'var(--gray-8)' }} />}
                        placeholder="e.g., Near Nakasero Market, Kampala"
                      />
                    </div>
                  )}

                  {currentStep === 4 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                      <div style={{
                        padding: '16px', borderRadius: '12px',
                        backgroundColor: 'var(--gray-2)',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <div>
                            <h3 style={{ fontSize: '15px', fontWeight: 600, color: 'var(--gray-12)', margin: 0 }}>I offer mobile services</h3>
                            <p style={{ fontSize: '13px', color: 'var(--gray-10)', margin: '4px 0 0' }}>I can travel to clients</p>
                          </div>
                          <input
                            type="checkbox"
                            name="offersMobile"
                            checked={formData.offersMobile}
                            onChange={handleChange}
                            style={{ width: '20px', height: '20px', accentColor: 'var(--orange-9)' }}
                          />
                        </div>

                        {formData.offersMobile && (
                          <div style={{ marginTop: '12px' }}>
                            <label style={{ display: 'block', fontSize: '13px', color: 'var(--gray-10)', marginBottom: '6px' }}>
                              Maximum travel distance (km)
                            </label>
                            <input
                              type="number"
                              name="maxTravelKm"
                              value={formData.maxTravelKm}
                              onChange={handleChange}
                              min={1}
                              max={100}
                              style={{
                                width: '100%', padding: '10px 12px', borderRadius: '8px',
                                border: '2px solid #f05a35', fontSize: '14px',
                                backgroundColor: '#ffffff',
                              }}
                            />
                          </div>
                        )}
                      </div>

                      <div style={{
                        padding: '16px', borderRadius: '12px',
                        backgroundColor: 'var(--gray-2)',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <div>
                            <h3 style={{ fontSize: '15px', fontWeight: 600, color: 'var(--gray-12)', margin: 0 }}>Auto-accept bookings</h3>
                            <p style={{ fontSize: '13px', color: 'var(--gray-10)', margin: '4px 0 0' }}>Automatically accept booking requests</p>
                          </div>
                          <input
                            type="checkbox"
                            name="autoAcceptBookings"
                            checked={formData.autoAcceptBookings}
                            onChange={handleChange}
                            style={{ width: '20px', height: '20px', accentColor: 'var(--orange-9)' }}
                          />
                        </div>
                      </div>

                      <Select
                        label="Buffer time between appointments"
                        name="bufferTimeMinutes"
                        value={formData.bufferTimeMinutes}
                        onChange={handleChange}
                        options={[
                          { value: 0, label: 'No buffer' },
                          { value: 5, label: '5 minutes' },
                          { value: 10, label: '10 minutes' },
                          { value: 15, label: '15 minutes' },
                          { value: 30, label: '30 minutes' },
                        ]}
                      />

                      <Select
                        label="Preferred client gender"
                        name="genderPreference"
                        value={formData.genderPreference}
                        onChange={handleChange}
                        options={GENDER_OPTIONS}
                      />

                      <div>
                        <label style={{ display: 'block', marginBottom: '4px', color: 'var(--gray-12)', fontSize: '14px', fontWeight: 500 }}>
                          Languages spoken
                        </label>
                        <p style={{ fontSize: '12px', color: 'var(--gray-9)', margin: '0 0 10px' }}>
                          English plus your region's language are pre-selected from your business location — tap to adjust.
                        </p>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                          {LANGUAGES.map((lang) => (
                            <button
                              key={lang.id}
                              type="button"
                              onClick={() => toggleLanguage(lang.id)}
                              style={{
                                padding: '8px 16px', borderRadius: '20px',
                                border: formData.languages.includes(lang.id) ? '2px solid var(--orange-9)' : '1px solid var(--gray-5)',
                                backgroundColor: formData.languages.includes(lang.id) ? 'var(--orange-1)' : 'transparent',
                                color: formData.languages.includes(lang.id) ? 'var(--orange-10)' : 'var(--gray-10)',
                                fontSize: '13px', fontWeight: 500,
                                cursor: 'pointer', transition: 'all 0.2s ease',
                              }}
                            >
                              {lang.name}
                            </button>
                          ))}
                        </div>
                      </div>

                      <Textarea
                        label="Hygiene & Safety Notes"
                        name="hygieneNotes"
                        value={formData.hygieneNotes}
                        onChange={handleChange}
                        placeholder="Any hygiene practices, safety measures, or health protocols clients should know..."
                        rows={3}
                      />
                    </div>
                  )}

                  {/* ── Step 5: Registration Fee ── */}
                  {currentStep === 5 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

                      {/* Fee summary card */}
                      <div style={{
                        border: '2px solid var(--orange-9)', borderRadius: '16px',
                        padding: '20px 24px', textAlign: 'center',
                        background: 'var(--orange-1)',
                      }}>
                        <p style={{ fontSize: '12px', fontWeight: 600, color: 'var(--orange-9)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 6px' }}>
                          {planLabel} Plan — Registration
                        </p>
                        <p style={{ fontSize: '34px', fontWeight: 800, color: 'var(--gray-12)', margin: '0 0 4px', fontFamily: "'Poppins', sans-serif" }}>
                          {planPriceText}
                        </p>
                        <p style={{ fontSize: '12px', color: 'var(--gray-10)', margin: 0 }}>
                          One payment activates your {planLabel} provider account
                        </p>
                      </div>

                      {/* Payment method selector */}
                      {!feeStatus && (
                        <div>
                          <p style={{ fontSize: '13px', fontWeight: 600, color: 'var(--gray-11)', margin: '0 0 10px', fontFamily: "'Poppins', sans-serif" }}>
                            Select payment method
                          </p>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>

                            {/* MTN Mobile Money */}
                            <button
                              type="button"
                              onClick={() => setPaymentMethod('mtn')}
                              style={{
                                padding: '14px 8px', borderRadius: '12px', cursor: 'pointer',
                                border: paymentMethod === 'mtn' ? '2px solid #FFCC00' : '2px solid var(--gray-4)',
                                background: paymentMethod === 'mtn' ? '#fffbeb' : 'var(--color-background)',
                                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px',
                                transition: 'all 0.15s ease',
                              }}
                            >
                              <img src={mtnLogo} alt="MTN" style={{ width: 56, height: 24, objectFit: 'contain' }} />
                              <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--gray-11)' }}>MTN MoMo</span>
                            </button>

                            {/* Airtel Money */}
                            <button
                              type="button"
                              onClick={() => setPaymentMethod('airtel')}
                              style={{
                                padding: '14px 8px', borderRadius: '12px', cursor: 'pointer',
                                border: paymentMethod === 'airtel' ? '2px solid #ED1C24' : '2px solid var(--gray-4)',
                                background: paymentMethod === 'airtel' ? '#fff1f1' : 'var(--color-background)',
                                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px',
                                transition: 'all 0.15s ease',
                              }}
                            >
                              <img src={airtelLogo} alt="Airtel" style={{ width: 64, height: 24, objectFit: 'contain' }} />
                              <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--gray-11)' }}>Airtel Money</span>
                            </button>

                            {/* Card */}
                            <button
                              type="button"
                              onClick={() => setPaymentMethod('card')}
                              style={{
                                padding: '14px 8px', borderRadius: '12px', cursor: 'pointer',
                                border: paymentMethod === 'card' ? '2px solid var(--gray-9)' : '2px solid var(--gray-4)',
                                background: paymentMethod === 'card' ? 'var(--gray-2)' : 'var(--color-background)',
                                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px',
                                transition: 'all 0.15s ease',
                              }}
                            >
                              <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'var(--gray-3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <CreditCardIcon style={{ width: 22, height: 22, color: 'var(--gray-11)' }} />
                              </div>
                              <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--gray-11)' }}>Card</span>
                            </button>

                          </div>

                          {/* Payment number — prefilled with registration number, editable */}
                          {paymentMethod !== 'card' && (
                            <div style={{ marginTop: '14px' }}>
                              <label style={{ display: 'block', marginBottom: '6px', color: 'var(--gray-12)', fontSize: '13px', fontWeight: 500 }}>
                                Number to pay with
                              </label>
                              <PhoneField
                                value={paymentPhone}
                                onChange={(value) => {
                                  setPaymentPhone(value || '');
                                  if (paymentPhoneError) setPaymentPhoneError('');
                                }}
                                error={paymentPhoneError}
                                className="phone-input-field"
                                style={{ width: '100%', borderRadius: '8px', backgroundColor: '#ffffff' }}
                              />
                              <PhoneAccountHint phone={paymentPhone} />
                              <p style={{ fontSize: '11px', color: 'var(--gray-9)', margin: '6px 0 0' }}>
                                The payment prompt goes to this number — it can be different from your registration number.
                              </p>
                            </div>
                          )}

                          {/* Helper text per method */}
                          <p style={{ fontSize: '12px', color: 'var(--gray-9)', margin: '10px 0 0', textAlign: 'center' }}>
                            {paymentMethod === 'mtn' && <>A prompt will be sent to <strong>{paymentPhone || formData.phone}</strong> via MTN MoMo</>}
                            {paymentMethod === 'airtel' && <>A prompt will be sent to <strong>{paymentPhone || formData.phone}</strong> via Airtel Money</>}
                            {paymentMethod === 'card' && 'You will be redirected to complete your card payment securely'}
                          </p>
                        </div>
                      )}

                      {/* Processing */}
                      {(feeStatus === 'PROCESSING' || feeStatus === 'PENDING') && (
                        <div style={{ textAlign: 'center', padding: '16px', borderRadius: '12px', background: 'var(--gray-2)' }}>
                          <div style={{
                            width: 32, height: 32, borderRadius: '50%', margin: '0 auto 10px',
                            border: '3px solid var(--orange-9)', borderTopColor: 'transparent',
                            animation: 'spin 0.9s linear infinite',
                          }} />
                          <p style={{ fontSize: '14px', color: 'var(--gray-11)', margin: 0 }}>
                            Waiting for payment confirmation…
                          </p>
                          <p style={{ fontSize: '12px', color: 'var(--gray-9)', margin: '4px 0 0' }}>
                            {paymentMethod === 'card'
                              ? 'Enter your card details on the payment page'
                              : 'Check your phone for the MoMo prompt'}
                          </p>

                          {/* Card: direct tap — mobile browsers block auto-opened tabs */}
                          {paymentMethod === 'card' && checkoutUrl && (
                            <a
                              href={checkoutUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                                marginTop: '14px', padding: '12px 24px', borderRadius: '10px',
                                background: 'linear-gradient(135deg, var(--orange-9), #f97316)',
                                color: '#fff', fontSize: '14px', fontWeight: 600,
                                textDecoration: 'none', fontFamily: "'Poppins', sans-serif",
                                boxShadow: '0 6px 18px rgba(240,90,53,0.35)',
                              }}
                            >
                              Open card payment page ↗
                            </a>
                          )}

                          {/* Polling gave up but the charge may have cleared — manual check */}
                          {!feePolling && (
                            <button
                              type="button"
                              onClick={checkFeeStatusOnce}
                              disabled={loading}
                              style={{
                                display: 'block', margin: '12px auto 0', padding: '10px 20px',
                                borderRadius: '10px', border: '1.5px solid var(--orange-9)',
                                background: 'transparent', color: 'var(--orange-9)',
                                fontSize: '13px', fontWeight: 600, cursor: 'pointer',
                                fontFamily: "'Poppins', sans-serif",
                              }}
                            >
                              Check payment status
                            </button>
                          )}
                        </div>
                      )}

                      {/* Completed */}
                      {feeStatus === 'COMPLETED' && (
                        <div style={{ textAlign: 'center', padding: '16px', borderRadius: '12px', background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                          <CheckCircleIcon style={{ width: 32, height: 32, color: '#16a34a', margin: '0 auto 8px' }} />
                          <p style={{ fontSize: '14px', fontWeight: 600, color: '#15803d', margin: 0 }}>
                            Payment confirmed! Submit your registration below.
                          </p>
                        </div>
                      )}

                      {/* Failed */}
                      {feeStatus === 'FAILED' && (
                        <div style={{ textAlign: 'center', padding: '12px', borderRadius: '12px', background: '#fef2f2', border: '1px solid #fecaca' }}>
                          <p style={{ fontSize: '14px', color: '#dc2626', margin: 0 }}>
                            Payment failed. Please try again.
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', marginTop: '8px' }}>
                    {currentStep > 1 ? (
                      <Button type="button" variant="outline" onClick={handleBack} disabled={feePolling}>
                        <ChevronLeftIcon style={{ width: '18px', height: '18px', marginRight: '6px' }} />
                        Back
                      </Button>
                    ) : (
                      <div />
                    )}

                    {currentStep < 4 && (
                      <Button type="button" onClick={handleNext}>
                        Next
                        <ChevronRightIcon style={{ width: '18px', height: '18px', marginLeft: '6px' }} />
                      </Button>
                    )}

                    {currentStep === 4 && (
                      <Button type="button" onClick={handleNext}>
                        Next
                        <ChevronRightIcon style={{ width: '18px', height: '18px', marginLeft: '6px' }} />
                      </Button>
                    )}

                    {currentStep === 5 && feeStatus !== 'COMPLETED' && (
                      <div style={{ display: 'flex', gap: '10px' }}>
                        {(feeStatus === 'PROCESSING' || feeStatus === 'PENDING' || feeStatus === 'FAILED') && (
                          <Button type="button" variant="outline" onClick={handleCancelPayment} disabled={loading}>
                            Cancel Payment
                          </Button>
                        )}
                        <Button type="button" onClick={handlePayFee} loading={loading || feePolling}>
                          {feeStatus === 'FAILED'
                            ? 'Retry Payment'
                            : paymentMethod === 'card'
                              ? `Pay with Card — ${planPriceText}`
                              : `Pay Now — ${planPriceText}`}
                        </Button>
                      </div>
                    )}

                    {currentStep === 5 && feeStatus === 'COMPLETED' && (
                      <Button type="submit" loading={loading}>
                        Submit Registration
                        <CheckCircleIcon style={{ width: '18px', height: '18px', marginLeft: '6px' }} />
                      </Button>
                    )}
                  </div>
                </form>
              )}
            </div>

            <div style={{ textAlign: 'center', marginTop: '28px' }}>
              <p style={{ fontSize: '14px', color: 'var(--gray-10)', margin: 0 }}>
                Already have an account?{' '}
                <Link to="/login" style={{ fontWeight: 600, color: 'var(--orange-9)', textDecoration: 'none' }}>Sign In</Link>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* RIGHT — Lottie panel */}
      <div
        className="hidden lg:flex flex-col items-center justify-center"
        style={{
          width: '45%',
          padding: '60px 48px',
          position: 'relative',
          overflow: 'hidden',
          backgroundColor: '#faf9fb',
        }}
      >
        {/* Soft background blob */}
        <div
          aria-hidden
          style={{
            position: 'absolute',
            width: '480px',
            height: '480px',
            borderRadius: '46% 54% 38% 62% / 56% 44% 56% 44%',
            backgroundColor: 'rgba(240, 90, 53, 0.08)',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -54%)',
            pointerEvents: 'none',
          }}
        />

        <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {/* Animation */}
          <div style={{ width: '340px', height: '340px', marginBottom: '32px' }}>
            <DotLottieReact
              src="https://lottie.host/e07c18d4-2397-4426-bb24-b79e68fa2f47/L23HjIxM3u.lottie"
              loop
              autoplay
              style={{ width: '100%', height: '100%' }}
            />
          </div>

          {/* Copy — changes with role */}
          <div style={{ maxWidth: '380px', textAlign: 'center' }}>
            <h2
              style={{
                fontSize: '26px', fontWeight: 700, color: 'var(--gray-12)',
                margin: '0 0 12px', lineHeight: 1.25, fontFamily: "'Poppins', sans-serif",
                transition: 'opacity 0.2s',
              }}
            >
              {accountType === 'provider'
                ? 'Start growing your business today'
                : 'Join a community that celebrates your look'}
            </h2>
            <p
              style={{
                fontSize: '14px', color: 'var(--gray-10)', lineHeight: 1.7,
                margin: 0, fontFamily: "'Poppins', sans-serif",
              }}
            >
              {accountType === 'provider'
                ? 'List your services, manage bookings, and get paid — all from one beautifully simple dashboard.'
                : 'Discover verified stylists, book in seconds, and keep every appointment and preference in one place.'}
            </p>

            {/* Floating trust badges */}
            <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '28px', flexWrap: 'wrap' }}>
              {(accountType === 'provider'
                ? ['Instant payouts', 'Client reviews', 'Booking calendar']
                : ['Verified providers', 'Secure payments', 'Easy rescheduling']
              ).map((badge) => (
                <span
                  key={badge}
                  style={{
                    fontSize: '11px', fontWeight: 600,
                    padding: '5px 12px', borderRadius: '999px',
                    backgroundColor: 'var(--orange-2)',
                    color: 'var(--orange-10)',
                    border: '1px solid var(--orange-4)',
                    fontFamily: "'Poppins', sans-serif",
                  }}
                >
                  {badge}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }

        .phone-input-field {
          border: 1px solid var(--gray-6);
          transition: border-color 0.15s ease, box-shadow 0.15s ease;
        }
        .phone-input-field:focus-within {
          border-color: #f05a35;
          box-shadow: 0 0 0 2px rgba(255,120,0,0.25);
        }
        .phone-input-field .PhoneInputInput {
          border: none;
          outline: none;
          background: transparent;
          font-size: 14px;
          color: #1f1f1f;
        }
      `}</style>
    </div>
  );
};

export default Register;
